import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { 
  format, parseISO, startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  eachDayOfInterval, addWeeks, subWeeks, addMonths, subMonths,
  isSameMonth, isToday, isBefore
} from 'date-fns';
import {
  ChevronLeft, ChevronRight, DollarSign, Clock,
  Users, Briefcase, MapPin, ExternalLink, AlertTriangle, FileText, CheckCircle2
} from 'lucide-react';
import { formatCurrency } from '../utils/helpers';
import { serviceTypes } from '../utils/jobData';
import './CalendarView.css';

const SERVICE_LABELS = {
  weekly: 'Weekly',
  biweekly: 'Bi-Weekly',
  monthly: 'Monthly',
  deep_clean: 'Deep Clean',
  move_in_out: 'Move In/Out',
  one_time: 'One-Time',
  laundry_pickup: 'Laundry'
};

const DEPOSIT_PRONE_SERVICES = new Set(['deep_clean', 'move_in_out', 'one_time']);

function CalendarView() {
  const navigate = useNavigate();
  const { state } = useApp();
  const [viewMode, setViewMode] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());

  const [showOnlyIssues, setShowOnlyIssues] = useState(false);

  const { startDate, endDate, days } = useMemo(() => {
    if (viewMode === 'week') {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      const end = endOfWeek(currentDate, { weekStartsOn: 1 });
      return { startDate: start, endDate: end, days: eachDayOfInterval({ start, end }) };
    } else {
      // month + billables both span a full month
      const start = startOfMonth(currentDate);
      const end = endOfMonth(currentDate);
      const calendarStart = startOfWeek(start, { weekStartsOn: 1 });
      const calendarEnd = endOfWeek(end, { weekStartsOn: 1 });
      return { startDate: start, endDate: end, days: eachDayOfInterval({ start: calendarStart, end: calendarEnd }) };
    }
  }, [viewMode, currentDate]);

  const jobsByDate = useMemo(() => {
    const grouped = {};
    state.jobs.forEach(job => {
      if (!job.date) return;
      if (!grouped[job.date]) grouped[job.date] = [];
      grouped[job.date].push(job);
    });
    // Sort each day's jobs by time
    Object.keys(grouped).forEach(date => {
      grouped[date].sort((a, b) => (a.scheduledTime || '09:00').localeCompare(b.scheduledTime || '09:00'));
    });
    return grouped;
  }, [state.jobs]);

  const viewStats = useMemo(() => {
    let totalJobs = 0, completedJobs = 0, totalRevenue = 0, totalHours = 0;
    days.forEach(day => {
      const dateKey = format(day, 'yyyy-MM-dd');
      const dayJobs = jobsByDate[dateKey] || [];
      if (viewMode === 'month' && !isSameMonth(day, currentDate)) return;
      dayJobs.forEach(job => {
        if (job.status === 'cancelled') return;
        totalJobs++;
        if (job.status === 'completed') completedJobs++;
        totalRevenue += job.price || 0;
        totalHours += job.duration || 0;
      });
    });
    return { totalJobs, completedJobs, totalRevenue, totalHours };
  }, [days, jobsByDate, viewMode, currentDate]);

  const goToPrevious = () => setCurrentDate(viewMode === 'week' ? subWeeks(currentDate, 1) : subMonths(currentDate, 1));
  const goToNext = () => setCurrentDate(viewMode === 'week' ? addWeeks(currentDate, 1) : addMonths(currentDate, 1));
  const goToToday = () => setCurrentDate(new Date());

  // ===== BILLABLES =====
  // Group invoices by jobId so we can match each scheduled job to its invoice(s).
  const invoicesByJobId = useMemo(() => {
    const map = {};
    (state.invoices || []).forEach(inv => {
      if (!inv.jobId) return;
      if (!map[inv.jobId]) map[inv.jobId] = [];
      map[inv.jobId].push(inv);
    });
    return map;
  }, [state.invoices]);

  // Build per-job billables rows for the current month, with payment-gap detection.
  const billablesRows = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const rows = [];

    state.jobs.forEach(job => {
      if (!job.date) return;
      if (job.status === 'cancelled') return;
      const d = parseISO(job.date);
      if (isBefore(d, monthStart) || isBefore(monthEnd, d)) return;

      const jobAmount = Number(job.price) || 0;
      const invs = (invoicesByJobId[job.id] || []).filter(i => i.status !== 'cancelled');
      const invoiced = invs.reduce((s, i) => s + (Number(i.total) || 0), 0);
      const paid = invs.reduce((s, i) => s + (Number(i.amountPaid) || 0), 0);
      const outstanding = Math.max(0, invoiced - paid);
      const anyDepositPaid = invs.some(i => i.depositPaid);
      const allPaid = invs.length > 0 && invs.every(i => i.status === 'paid' || (Number(i.balance) || 0) <= 0);

      // Determine flag (worst-case wins)
      let flag = null;
      if (jobAmount > 0 && invs.length === 0) {
        flag = { kind: 'not_invoiced', label: 'Not invoiced' };
      } else if (jobAmount > 0 && invoiced + 0.01 < jobAmount) {
        flag = { kind: 'underbilled', label: 'Underbilled' };
      } else if (anyDepositPaid && outstanding > 0) {
        // Deposit paid but balance never collected — the case the user is hunting for
        flag = { kind: 'deposit_only', label: 'Deposit paid, balance owed' };
      } else if (outstanding > 0 && DEPOSIT_PRONE_SERVICES.has(job.serviceType)) {
        flag = { kind: 'deep_clean_unpaid', label: 'Deep clean — unpaid' };
      } else if (outstanding > 0) {
        flag = { kind: 'unpaid', label: 'Unpaid' };
      } else if (allPaid) {
        flag = { kind: 'paid', label: 'Paid' };
      }

      rows.push({
        job,
        invoices: invs,
        jobAmount,
        invoiced,
        paid,
        outstanding,
        flag,
        depositPaid: anyDepositPaid
      });
    });

    // Sort: issues first (by severity), then by date desc
    const severity = {
      not_invoiced: 0,
      deposit_only: 1,
      deep_clean_unpaid: 2,
      underbilled: 3,
      unpaid: 4,
      paid: 5
    };
    rows.sort((a, b) => {
      const sa = a.flag ? (severity[a.flag.kind] ?? 9) : 9;
      const sb = b.flag ? (severity[b.flag.kind] ?? 9) : 9;
      if (sa !== sb) return sa - sb;
      return (b.job.date || '').localeCompare(a.job.date || '');
    });

    return rows;
  }, [state.jobs, invoicesByJobId, currentDate]);

  const billablesTotals = useMemo(() => {
    return billablesRows.reduce((acc, r) => {
      acc.jobAmount += r.jobAmount;
      acc.invoiced += r.invoiced;
      acc.paid += r.paid;
      acc.outstanding += r.outstanding;
      if (r.flag && r.flag.kind !== 'paid') acc.issueCount += 1;
      return acc;
    }, { jobAmount: 0, invoiced: 0, paid: 0, outstanding: 0, issueCount: 0 });
  }, [billablesRows]);

  const visibleBillablesRows = useMemo(() => {
    if (!showOnlyIssues) return billablesRows;
    return billablesRows.filter(r => r.flag && r.flag.kind !== 'paid');
  }, [billablesRows, showOnlyIssues]);

  const formatTime = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const hour = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${hour}:${String(m).padStart(2, '0')} ${period}`;
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'completed': return 'completed';
      case 'in_progress': return 'in-progress';
      case 'scheduled': return 'scheduled';
      case 'en_route': return 'en-route';
      case 'cancelled': return 'cancelled';
      default: return 'scheduled';
    }
  };

  const navigateToDispatch = (dateKey) => {
    navigate('/admin/dispatch', { state: { selectedDate: dateKey } });
  };

  // ===== WEEK VIEW =====
  const renderWeekView = () => (
    <div className="cal-week">
      {days.map(day => {
        const dateKey = format(day, 'yyyy-MM-dd');
        const dayJobs = (jobsByDate[dateKey] || []).filter(j => j.status !== 'cancelled');
        const dayRevenue = dayJobs.reduce((sum, j) => sum + (j.price || 0), 0);
        const dayHours = dayJobs.reduce((sum, j) => sum + (j.duration || 0), 0);
        const isPast = isBefore(day, new Date()) && !isToday(day);
        const isWeekend = day.getDay() === 0 || day.getDay() === 6;

        return (
          <div key={dateKey} className={`cal-day-col ${isToday(day) ? 'today' : ''} ${isPast ? 'past' : ''} ${isWeekend ? 'weekend' : ''}`}>
            {/* Day Header */}
            <div className="cal-day-header" onClick={() => navigateToDispatch(dateKey)}>
              <div className="cal-day-label">
                <span className="cal-day-name">{format(day, 'EEE')}</span>
                <span className={`cal-day-num ${isToday(day) ? 'today-num' : ''}`}>{format(day, 'd')}</span>
              </div>
              {dayJobs.length > 0 && (
                <div className="cal-day-stats">
                  <span className="cal-job-count">{dayJobs.length} job{dayJobs.length !== 1 ? 's' : ''}</span>
                  <span className="cal-day-rev">${dayRevenue.toLocaleString()}</span>
                </div>
              )}
              <ExternalLink size={14} className="cal-dispatch-link" />
            </div>

            {/* Jobs List */}
            <div className="cal-day-jobs">
              {dayJobs.length === 0 ? (
                <div className="cal-no-jobs">{isWeekend ? 'Weekend' : 'No jobs'}</div>
              ) : (
                dayJobs.map(job => (
                  <div 
                    key={job.id} 
                    className={`cal-job ${getStatusClass(job.status)}`}
                    onClick={() => navigateToDispatch(dateKey)}
                  >
                    <div className="cal-job-time">{formatTime(job.scheduledTime)}</div>
                    <div className="cal-job-info">
                      <span className="cal-job-client">{job.clientName}</span>
                      {job.teamNames && job.teamNames.length > 0 && (
                        <span className="cal-job-team">{job.teamNames.join(', ')}</span>
                      )}
                    </div>
                    <div className="cal-job-right">
                      <span className="cal-job-price">${job.price || 0}</span>
                      <span className="cal-job-dur">{job.duration}h</span>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Day Footer */}
            {dayJobs.length > 0 && (
              <div className="cal-day-footer">
                <span>{dayHours}h total</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // ===== MONTH VIEW =====
  const renderMonthView = () => {
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }

    return (
      <div className="cal-month">
        <div className="cal-month-header">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
            <div key={d} className="cal-month-hcell">{d}</div>
          ))}
        </div>
        <div className="cal-month-body">
          {weeks.map((week, wi) => (
            <div key={wi} className="cal-month-row">
              {week.map(day => {
                const dateKey = format(day, 'yyyy-MM-dd');
                const dayJobs = (jobsByDate[dateKey] || []).filter(j => j.status !== 'cancelled');
                const inMonth = isSameMonth(day, currentDate);
                const dayRevenue = dayJobs.reduce((sum, j) => sum + (j.price || 0), 0);

                return (
                  <div
                    key={dateKey}
                    className={`cal-month-cell ${isToday(day) ? 'today' : ''} ${!inMonth ? 'outside' : ''} ${dayJobs.length > 0 ? 'has-jobs' : ''}`}
                    onClick={() => {
                      if (dayJobs.length > 0) {
                        navigateToDispatch(dateKey);
                      } else {
                        setViewMode('week');
                        setCurrentDate(day);
                      }
                    }}
                  >
                    <span className={`cal-mcell-num ${isToday(day) ? 'today-badge' : ''}`}>
                      {format(day, 'd')}
                    </span>
                    {inMonth && dayJobs.length > 0 && (
                      <div className="cal-mcell-content">
                        <span className="cal-mcell-count">{dayJobs.length} job{dayJobs.length !== 1 ? 's' : ''}</span>
                        <span className="cal-mcell-rev">${dayRevenue.toLocaleString()}</span>
                        <div className="cal-mcell-dots">
                          {dayJobs.slice(0, 5).map(job => (
                            <span key={job.id} className={`cal-dot ${getStatusClass(job.status)}`} />
                          ))}
                          {dayJobs.length > 5 && <span className="cal-dot-more">+{dayJobs.length - 5}</span>}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ===== BILLABLES VIEW =====
  const renderBillablesView = () => {
    if (visibleBillablesRows.length === 0) {
      return (
        <div className="cal-billables-empty">
          {billablesRows.length === 0
            ? `No jobs scheduled in ${format(currentDate, 'MMMM yyyy')}.`
            : 'No billing issues for this month — everything is invoiced and paid.'}
        </div>
      );
    }
    return (
      <div className="cal-billables">
        <table className="cal-bill-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Client</th>
              <th>Service</th>
              <th className="num">Job Amount</th>
              <th className="num">Invoiced</th>
              <th className="num">Paid</th>
              <th className="num">Outstanding</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {visibleBillablesRows.map(r => {
              const { job, invoices, jobAmount, invoiced, paid, outstanding, flag } = r;
              const primaryInvoice = invoices[0];
              const goRow = () => {
                if (primaryInvoice) {
                  navigate(`/admin/invoices/${primaryInvoice.id}`);
                } else {
                  navigate('/admin/dispatch', { state: { selectedDate: job.date } });
                }
              };
              return (
                <tr
                  key={job.id}
                  className={`cal-bill-row ${flag ? `flag-${flag.kind}` : ''}`}
                  onClick={goRow}
                >
                  <td>{format(parseISO(job.date), 'MMM d')}</td>
                  <td className="cal-bill-client">{job.clientName || '—'}</td>
                  <td>{SERVICE_LABELS[job.serviceType] || job.serviceType || '—'}</td>
                  <td className="num">{formatCurrency(jobAmount)}</td>
                  <td className="num">
                    {invoices.length === 0
                      ? <span className="cal-bill-muted">—</span>
                      : formatCurrency(invoiced)}
                  </td>
                  <td className="num">{formatCurrency(paid)}</td>
                  <td className={`num ${outstanding > 0 ? 'cal-bill-outstanding' : ''}`}>
                    {formatCurrency(outstanding)}
                  </td>
                  <td>
                    {flag && (
                      <span className={`cal-bill-pill pill-${flag.kind}`}>
                        {flag.kind === 'paid'
                          ? <CheckCircle2 size={13} />
                          : flag.kind === 'not_invoiced'
                            ? <FileText size={13} />
                            : <AlertTriangle size={13} />}
                        {flag.label}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3}>Totals ({visibleBillablesRows.length} job{visibleBillablesRows.length !== 1 ? 's' : ''})</td>
              <td className="num">{formatCurrency(visibleBillablesRows.reduce((s, r) => s + r.jobAmount, 0))}</td>
              <td className="num">{formatCurrency(visibleBillablesRows.reduce((s, r) => s + r.invoiced, 0))}</td>
              <td className="num">{formatCurrency(visibleBillablesRows.reduce((s, r) => s + r.paid, 0))}</td>
              <td className="num">{formatCurrency(visibleBillablesRows.reduce((s, r) => s + r.outstanding, 0))}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  return (
    <div className="calendar-page">
      {/* Header */}
      <div className="cal-header-bar">
        <div>
          <h1 className="cal-title">Calendar</h1>
          <p className="cal-subtitle">
            {viewMode === 'week'
              ? `Week of ${format(startDate, 'MMM d')} – ${format(endDate, 'MMM d, yyyy')}`
              : viewMode === 'billables'
                ? `Billables — ${format(currentDate, 'MMMM yyyy')}`
                : format(currentDate, 'MMMM yyyy')
            }
          </p>
        </div>
        <div className="cal-nav">
          <div className="cal-view-toggle">
            <button className={viewMode === 'week' ? 'active' : ''} onClick={() => setViewMode('week')}>Week</button>
            <button className={viewMode === 'month' ? 'active' : ''} onClick={() => setViewMode('month')}>Month</button>
            <button className={viewMode === 'billables' ? 'active' : ''} onClick={() => setViewMode('billables')}>Billables</button>
          </div>
          <div className="cal-arrows">
            <button onClick={goToPrevious}><ChevronLeft size={20} /></button>
            <button className="cal-today-btn" onClick={goToToday}>Today</button>
            <button onClick={goToNext}><ChevronRight size={20} /></button>
          </div>
        </div>
      </div>

      {/* Stats */}
      {viewMode === 'billables' ? (
        <div className="cal-stats">
          <div className="cal-stat">
            <Briefcase size={18} />
            <div><strong>{formatCurrency(billablesTotals.jobAmount)}</strong><span>Job Amount</span></div>
          </div>
          <div className="cal-stat">
            <FileText size={18} />
            <div><strong>{formatCurrency(billablesTotals.invoiced)}</strong><span>Invoiced</span></div>
          </div>
          <div className="cal-stat highlight">
            <DollarSign size={18} />
            <div><strong>{formatCurrency(billablesTotals.paid)}</strong><span>Paid</span></div>
          </div>
          <div className={`cal-stat ${billablesTotals.outstanding > 0 ? 'warn' : ''}`}>
            <AlertTriangle size={18} />
            <div><strong>{formatCurrency(billablesTotals.outstanding)}</strong><span>Outstanding</span></div>
          </div>
        </div>
      ) : (
        <div className="cal-stats">
          <div className="cal-stat">
            <Briefcase size={18} />
            <div><strong>{viewStats.totalJobs}</strong><span>Jobs</span></div>
          </div>
          <div className="cal-stat highlight">
            <DollarSign size={18} />
            <div><strong>{formatCurrency(viewStats.totalRevenue)}</strong><span>Revenue</span></div>
          </div>
          <div className="cal-stat">
            <Clock size={18} />
            <div><strong>{viewStats.totalHours}h</strong><span>Hours</span></div>
          </div>
          <div className="cal-stat">
            <Users size={18} />
            <div><strong>{viewStats.completedJobs}</strong><span>Done</span></div>
          </div>
        </div>
      )}

      {/* Billables filter bar */}
      {viewMode === 'billables' && (
        <div className="cal-bill-filter">
          <label className="cal-bill-toggle">
            <input
              type="checkbox"
              checked={showOnlyIssues}
              onChange={e => setShowOnlyIssues(e.target.checked)}
            />
            Show only issues ({billablesTotals.issueCount})
          </label>
          <span className="cal-bill-hint">
            Click a row to open the invoice (or jump to dispatch if no invoice exists yet).
          </span>
        </div>
      )}

      {/* Calendar */}
      <div className="cal-body">
        {viewMode === 'week' && renderWeekView()}
        {viewMode === 'month' && renderMonthView()}
        {viewMode === 'billables' && renderBillablesView()}
      </div>

      {/* Legend */}
      {viewMode !== 'billables' && (
        <div className="cal-legend">
          <span><i className="dot scheduled" /> Scheduled</span>
          <span><i className="dot in-progress" /> In Progress</span>
          <span><i className="dot completed" /> Completed</span>
        </div>
      )}
    </div>
  );
}

export default CalendarView;
